import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, useRef, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { brokerOrgJsonLd } from "@/lib/seo-schema";
import { Toaster } from "@/components/ui/sonner";
import { CookieConsent } from "@/components/site/CookieConsent";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { LOCALE_META, localeFromPath } from "@/lib/i18n/locale";
import { pageDatesQuery, type PageDatesMap } from "@/lib/page-freshness.functions";
import { hasOwnDateSource, routeKeyFromMatches } from "@/lib/seo/page-freshness";

/** Meta + WebPage schema dates from the same page_freshness record as the visible badge. */
function pageDateTags(matches: ReadonlyArray<{ routeId: string; pathname: string }>, map: PageDatesMap | undefined) {
  const key = routeKeyFromMatches(matches);
  if (!key || hasOwnDateSource(key) || !map?.[key]) return { meta: [], scripts: [] };
  const { published, reviewed } = map[key];
  if (!published && !reviewed) return { meta: [], scripts: [] };
  const path = matches[matches.length - 1]?.pathname ?? "/";
  const url = `https://dscrloanmarket.com${path === "/" ? "/" : path.replace(/\/$/, "")}`;
  return {
    meta: [
      ...(published ? [{ property: "article:published_time", content: published }] : []),
      ...(reviewed ? [{ property: "article:modified_time", content: reviewed }] : []),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": url,
          url,
          ...(published ? { datePublished: published } : {}),
          ...(reviewed ? { dateModified: reviewed, lastReviewed: reviewed } : {}),
        }),
      },
    ],
  };
}



function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-brand">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-brand-gold px-4 py-2 text-sm font-semibold text-primary hover:bg-brand-gold-hover"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error as Error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. Try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-brand px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-brand-dark"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

const ORG_JSONLD = {
  "@context": "https://schema.org",
  ...brokerOrgJsonLd(),
  description:
    "Mortgage broker arranging DSCR and business-purpose loans for real estate investors — DSCR, Foreign National, Construction, Fix & Flip, Airbnb / STR, and Section 8 DSCR loans.",
  serviceType: [
    "DSCR loans",
    "Foreign National loans",
    "Construction loans",
    "Fix & Flip loans",
    "Airbnb / Short-Term Rental loans",
    "Section 8 DSCR loans",
  ],
};

const WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Simply Approved Mortgages — DSCR",
  url: "https://dscrloanmarket.com/",
  publisher: { "@id": "https://dscrloanmarket.com/#organization" },
};

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  loader: ({ context }) => context.queryClient.ensureQueryData(pageDatesQuery),
  head: ({ matches, loaderData }) => {
    const dated = pageDateTags(matches, loaderData);
    return {
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "DSCR Business-Purpose Loans | Simply Approved Mortgages" },
      {
        name: "description",
        content:
          "Licensed in Florida and Colorado. DSCR, Foreign National, Construction, Fix & Flip, STR, Section 8 DSCR loans for investors. LLC vesting. No tax returns.",
      },
      { name: "author", content: "Simply Approved Mortgages" },
      { property: "og:site_name", content: "Simply Approved Mortgages" },
      { property: "og:title", content: "DSCR Business-Purpose Loans | Simply Approved Mortgages" },
      {
        property: "og:description",
        content:
          "Licensed in FL & CO. DSCR, Foreign National, Construction, Fix & Flip, STR, and Section 8 DSCR loans for investors. LLC vesting. No tax returns.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      ...dated.meta,
      {
        name: "keywords",
        content:
          "DSCR loans, business-purpose loans, investment property loans, rental property loans, Florida DSCR lender, Colorado DSCR lender, LLC loans, no tax return loans, foreign national loans, construction loans, fix and flip loans, Airbnb loans, STR loans, Section 8 DSCR loans",
      },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.png", type: "image/png" },
      { rel: "alternate", type: "application/rss+xml", title: "DSCR Loan Market Investor Blog", href: "https://dscrloanmarket.com/rss.xml" },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/inter-var.woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/plus-jakarta-sans-var.woff2",
        crossOrigin: "anonymous",
      },
    ],
    scripts: [
      // No tracking scripts in the HTML: Meta Pixel and Google Analytics load
      // only after the visitor clicks "Accept all" on the cookie banner.
      { type: "application/ld+json", children: JSON.stringify(ORG_JSONLD) },
      { type: "application/ld+json", children: JSON.stringify(WEBSITE_JSONLD) },
      ...dated.scripts,
    ],
  };
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const lang = LOCALE_META[localeFromPath(pathname)].htmlLang;
  return (
    <html lang={lang}>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    // Capture first-touch UTMs once per session so any downstream lead
    // form submission includes attribution even after intra-site navigation.
    void import("@/lib/utm").then((m) => m.captureUtmParams());
    let started = false;
    const start = async () => {
      if (started || localStorage.getItem("sam_cookie_consent_v1") !== "all") return;
      started = true;
      const [m, cfg] = await Promise.all([
        import("@/lib/tracking"),
        import("@/lib/analytics-config.functions")
          .then((c) => c.getAnalyticsConfig())
          .catch(() => ({ ga4Id: null, metaPixelId: null })),
      ]);
      m.initTracking(cfg.ga4Id, cfg.metaPixelId);
      if (window.gtag && m.GA4_ID) {
        const g = window.gtag as unknown as (...a: unknown[]) => void;
        g("consent", "update", {
          analytics_storage: "granted",
          ad_storage: "granted",
          ad_user_data: "granted",
          ad_personalization: "granted",
        });
        g("event", "page_view", {
          page_path: window.location.pathname,
          page_location: window.location.href,
          page_title: document.title,
        });
      }
    };
    void start();
    const onConsent = () => void start();
    window.addEventListener("sam-consent-change", onConsent);
    return () => window.removeEventListener("sam-consent-change", onConsent);
  }, []);
  const firstPath = useRef(true);
  useEffect(() => {
    // SPA route change -> GA4 page_view + Meta PageView (first view sent after init).
    if (firstPath.current) { firstPath.current = false; return; }
    void import("@/lib/tracking").then((m) => m.trackPageView(pathname));
  }, [pathname]);
  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
      <Toaster />
      <CookieConsent />
    </QueryClientProvider>

  );
}

